import { useMemo } from 'react'
import AddIcon from '@mui/icons-material/Add'
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useLocalStorage from '@/hooks/useLocalStorage'
import useOverlay from '@/hooks/useOverlay'
import { useGetDocumentWorkflowStatesCount } from '@/service-library/hooks/document-workflow-states'
import { useGetProjectCategories } from '@/service-library/hooks/project-categories'
import { useGetProjectTemplates } from '@/service-library/hooks/project-templates'
import { useGetProjects } from '@/service-library/hooks/projects'
import { useGetWorkflows } from '@/service-library/hooks/workflows'
import { ProjectCategory as ProjectCategoryT } from '@/types/projects'
import AutoAssigningDocumentsCard from '@/components/auto-assigning-documents-card/AutoAssigningDocumentsCard'
import OrphanedDocumentsCard from '@/components/orphaned-documents-card/OrphanedDocumentsCard'
import PageTitle from '@/components/PageTitle'
import { ProjectForCard } from '@/components/project-card/ProjectCard'
import AddProjectCategoryDialog from '@/components/projects/AddProjectCategoryDialog'
import ProjectCategory from '@/components/projects/ProjectCategory'
import UploadCard from '@/components/upload-card/UploadCard'
import UploadingFilesCard from '@/components/uploading-files-card/UploadingFilesCard'

export default function ProjectsPage() {
  const featureFlags = useFeatureFlagContext()
  const { isAuthorized: canAddEdit } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const [autoAssignCardVisibility] = useLocalStorage(
    'auto-assign-card-visibility',
    'show',
  )

  const {
    queryKey,
    projectCategories = [],
    isError: categoriesError,
    isPending: categoriesIsLoading,
  } = useGetProjectCategories({
    filters: {
      limit: '1000',
    },
  })

  const {
    projects = [],
    isError: projectsError,
    isPending: projectsIsLoading,
  } = useGetProjects({
    filters: {
      limit: '1000',
      fields__include: 'project_category,has_pub_models',
    },
  })

  const { workflows } = useGetWorkflows({
    filters: {
      limit: '1000',
      code: 'basic',
      fields__only: 'id,project_id',
    },
  })

  const projectWorkflowsMap = useMemo(() => {
    return workflows.reduce<Record<string, string>>((acc, workflow) => {
      acc[workflow.project_id] = workflow.id
      return acc
    }, {})
  }, [workflows])

  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1000',
      fields__only: 'project_id',
    },
  })

  const projectTemplatesSet = useMemo(() => {
    return new Set(projectTemplates.map((template) => template.project_id))
  }, [projectTemplates])

  const addCategoryOverlay = useOverlay()

  const isPending = projectsIsLoading || categoriesIsLoading
  const isError = categoriesError || projectsError

  const categoriesWithProjects = useMemo(() => {
    if (!isPending && projectCategories.length > 0) {
      const allCategoriesObj: Record<
        string,
        ProjectCategoryT & { projects: ProjectForCard[] }
      > = [...projectCategories].reduce(
        (all, category) => ({
          ...all,
          [category.id]: { ...category, projects: [] },
        }),
        {},
      )
      projects.forEach((project) => {
        const categoryId = project.project_category_id
        allCategoriesObj[categoryId]?.projects.push({
          ...project,
          basic_workflow_id: projectWorkflowsMap[project.id],
          has_templates: projectTemplatesSet.has(project.id),
        })
      })

      return Object.values(allCategoriesObj)
    }
    return []
  }, [
    isPending,
    projectCategories,
    projects,
    projectWorkflowsMap,
    projectTemplatesSet,
  ])

  const { groupedCounts } = useGetDocumentWorkflowStatesCount({
    filters: {
      workflow_state__workflow_state_type__code: 'in_progress',
      '~group-by': 'document__project_id',
    },
    refetchInterval: 60000,
  })

  const categoryCountsGrouped = useMemo(() => {
    const groupedCountMap = groupedCounts.reduce<Record<string, number>>(
      (acc, item) => {
        acc[item.document__project_id] = item.count
        return acc
      },
      {},
    )
    return projects.reduce<Record<string, Record<string, number>>>(
      (acc, project) => {
        acc[project.project_category_id] ||= {}
        acc[project.project_category_id][project.id] =
          groupedCountMap[project.id] || 0
        return acc
      },
      {},
    )
  }, [groupedCounts, projects])

  return (
    <>
      <PageTitle>Projects</PageTitle>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={4}>
          {featureFlags.auto_sort_documents_card && (
            <Stack spacing={1} sx={{ '&:empty': { mt: -4 } }}>
              <UploadCard />
              <UploadingFilesCard />
              {autoAssignCardVisibility === 'show' && (
                <AutoAssigningDocumentsCard />
              )}
              <OrphanedDocumentsCard />
            </Stack>
          )}
          {isPending && (
            <Typography textAlign="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Typography>
          )}
          {isError && (
            <Alert severity="error">
              Unable to load projects and categories. Please try again later.
            </Alert>
          )}
          {!isPending && !isError && (
            <>
              {categoriesWithProjects.map((category) => (
                <ProjectCategory
                  key={category.id}
                  canAddEdit={canAddEdit}
                  category={category}
                  projectsCount={categoryCountsGrouped[category.id]}
                />
              ))}

              {canAddEdit && (
                <div>
                  <Button
                    onClick={addCategoryOverlay.open}
                    startIcon={<AddIcon />}
                    variant="text"
                  >
                    New Category
                  </Button>
                </div>
              )}
            </>
          )}
        </Stack>

        {canAddEdit && (
          <AddProjectCategoryDialog
            overlay={addCategoryOverlay}
            listQueryKey={queryKey}
          />
        )}
      </Container>
    </>
  )
}
